import { CloseOutlined } from '@ant-design/icons';
import {
  IssueAmountStructure,
  IssueCaseCategory,
  IssueCaseRequestCategory,
  IssueCaseStatusCode,
  IssueImage,
  IssueLiabilityDoc,
  IssueLiabilityResponsibleType,
  IssueProduct,
  IssueTaskCategory,
  IssueTaskDeadline,
  IssueTaskDeadlineOption,
  IssueTaskInventoryIssueInfo,
  issueLiabilityResponsibleTypeMap,
} from '@gooduncles/gu-app-schema';
import { ICellRendererParams } from 'ag-grid-community';
import { Badge, Button, Image, Modal, Popconfirm, Popover, Spin, Table, Tag, Tooltip, message } from 'antd';
import { formatDate } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { issueTaskCategoryKr } from 'src/constants/issue.constants';

import { deleteIssueLiability } from 'src/utils/firebase/firebase-short-cut';

import useUserById from 'src/hooks/swr/useUserById';

import { taskCategoryColorMap } from './issue-ag-grid-util';
import { getCategoryLabel } from './issue-data-util';

/**
 * 금액을 포맷팅하는 헬퍼 함수
 */
const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
  }).format(amount);
};

/**
 * 카테고리 + 태스크 개수를 표시하는 innerRenderer 생성
 * agGroupCellRenderer의 innerRenderer로 사용
 * @param options.onRowClick - 행 클릭 시 호출될 콜백 (있으면 클릭 가능)
 */
export const createCategoryWithTaskCountRenderer = <
  T extends {
    category?: IssueCaseCategory | IssueCaseRequestCategory;
    displayCategory?: string;
    issueTasks?: unknown[];
  }
>(options?: {
  onRowClick?: (data: T) => void;
  showTaskCount?: boolean;
}) => {
  const { onRowClick, showTaskCount = true } = options || {};

  const CategoryWithTaskCountRenderer = (params: ICellRendererParams<T>) => {
    const { data } = params;
    const field = params.colDef?.field;
    // displayCategory 필드인 경우 값 그대로 사용, category 필드인 경우 한글 변환
    const categoryText =
      field === 'displayCategory' ? params.value || '-' : data?.category ? getCategoryLabel(data.category) : '-';
    const taskCount = data?.issueTasks?.length ?? 0;
    const displayText = showTaskCount ? `${categoryText} (${taskCount})` : categoryText;

    // 클릭 콜백이 없거나 데이터가 없으면 텍스트만 반환
    if (!onRowClick || !data) {
      return displayText;
    }

    return (
      <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => onRowClick(data)}>
        {displayText}
      </span>
    );
  };
  return CategoryWithTaskCountRenderer;
};

/**
 * 상태 + 등록일시 요약을 표시하는 cellRenderer 생성
 * 컬럼 그룹이 닫혀있을 때 사용
 */
export const createStatusDateSummaryCellRenderer = <T extends { status?: IssueCaseStatusCode; registeredAt?: string }>(
  statusBadgeMap: Record<
    IssueCaseStatusCode,
    { status: 'success' | 'processing' | 'warning' | 'error' | 'default'; text: string }
  >
) => {
  const StatusDateSummaryCellRenderer = (params: ICellRendererParams<T>) => {
    const status = params.data?.status;
    const registeredAt = params.data?.registeredAt;

    const statusText = status ? statusBadgeMap[status]?.text || status : '-';
    const dateText = registeredAt ? formatDate(new Date(registeredAt), 'MM-dd') : '-';

    const badgeInfo = status ? statusBadgeMap[status] : null;

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {badgeInfo ? <Badge status={badgeInfo.status} text={statusText} /> : statusText}
        <span style={{ color: '#8c8c8c', fontSize: 12 }}>({dateText})</span>
      </span>
    );
  };
  return StatusDateSummaryCellRenderer;
};

/**
 * Task용 상태 + 일시 요약을 표시하는 cellRenderer 생성
 * - 완료 상태(COMPLETED=50)일 때: finishedAt 표시
 * - 그 외: registeredAt 표시
 */
export const createTaskStatusDateSummaryCellRenderer = <
  T extends { status?: number; registeredAt?: string; finishedAt?: string | null }
>(
  statusBadgeMap: Record<number, { status: 'success' | 'processing' | 'warning' | 'error' | 'default'; text: string }>
) => {
  const COMPLETED_STATUS = 50;

  const TaskStatusDateSummaryCellRenderer = (params: ICellRendererParams<T>) => {
    const status = params.data?.status;
    const registeredAt = params.data?.registeredAt;
    const finishedAt = params.data?.finishedAt;

    const statusText = status ? statusBadgeMap[status]?.text || String(status) : '-';
    const isCompleted = status === COMPLETED_STATUS;
    const finishedAtText = finishedAt ? formatDate(new Date(finishedAt), 'MM-dd') : '-';
    const registeredAtText = registeredAt ? formatDate(new Date(registeredAt), 'MM-dd') : '-';

    const badgeInfo = status ? statusBadgeMap[status] : null;

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {badgeInfo ? <Badge status={badgeInfo.status} text={statusText} /> : statusText}
        <span style={{ color: '#8c8c8c', fontSize: 12 }}>
          ({registeredAtText}
          {isCompleted ? `/${finishedAtText}` : ''})
        </span>
      </span>
    );
  };
  return TaskStatusDateSummaryCellRenderer;
};

/**
 * Deadline 옵션 한글 매핑
 */
const deadlineOptionKrMap: Record<IssueTaskDeadlineOption, string> = {
  [IssueTaskDeadlineOption.ASAP]: '가능한 빨리',
  [IssueTaskDeadlineOption.BEFORE_DATE]: '전',
  [IssueTaskDeadlineOption.SPECIFIC_DATE]: '',
};

/**
 * Deadline/수행일정 요약을 표시하는 cellRenderer 생성
 * - targetDate가 있으면: "확정 11/25" (deadline은 흐리게)
 * - targetDate가 없으면:
 *   - ASAP: "가능한 빨리"
 *   - BEFORE_DATE: "~11/25 전"
 *   - SPECIFIC_DATE: "11/25"
 */
export const createDeadlineSummaryCellRenderer = <
  T extends { deadline?: IssueTaskDeadline | null; targetDate?: string | null }
>() => {
  const DeadlineSummaryCellRenderer = (params: ICellRendererParams<T>) => {
    const deadline = params.data?.deadline;
    const targetDate = params.data?.targetDate;

    // targetDate가 있으면 확정일 표시
    if (targetDate) {
      const targetDateText = formatDate(new Date(targetDate), 'MM/dd');
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#1890ff', fontWeight: 500 }}>확정 {targetDateText}</span>
        </span>
      );
    }

    // deadline만 있는 경우
    if (!deadline) return <span style={{ color: '#8c8c8c' }}>-</span>;

    const { option, value } = deadline;
    const optionText = deadlineOptionKrMap[option] || option;

    let displayText = '';
    if (option === IssueTaskDeadlineOption.ASAP) {
      displayText = optionText;
    } else if (option === IssueTaskDeadlineOption.BEFORE_DATE && value) {
      const dateText = formatDate(new Date(value), 'MM/dd');
      displayText = `~${dateText} ${optionText}`;
    } else if (option === IssueTaskDeadlineOption.SPECIFIC_DATE && value) {
      displayText = formatDate(new Date(value), 'MM/dd');
    } else {
      displayText = '-';
    }

    return <span>{displayText}</span>;
  };
  return DeadlineSummaryCellRenderer;
};

/**
 * Deadline option을 한글로 표시하는 valueFormatter
 */
export const deadlineOptionValueFormatter = (params: { value: IssueTaskDeadlineOption | null | undefined }) => {
  if (!params.value) return '-';
  const fullOptionKrMap: Record<IssueTaskDeadlineOption, string> = {
    [IssueTaskDeadlineOption.ASAP]: '가능한 빨리',
    [IssueTaskDeadlineOption.BEFORE_DATE]: '특정일 전',
    [IssueTaskDeadlineOption.SPECIFIC_DATE]: '특정일',
  };
  return fullOptionKrMap[params.value] || params.value;
};

/**
 * targetDate가 있을 때 deadline 컬럼의 스타일을 흐리게 처리
 */
export const createDeadlineDisabledCellStyle = () => {
  return (params: { data?: { targetDate?: string | null } }) => {
    if (params.data?.targetDate) {
      return { opacity: 0.4, color: '#8c8c8c' };
    }
    return undefined;
  };
};

/**
 * Badge를 사용하는 status cellRenderer를 생성하는 헬퍼 함수
 */
export const createStatusCellRenderer = <T extends Record<string, any>>(
  badgeMap: Record<string, { status: any; text: string }>
) => {
  const StatusCellRenderer = (params: ICellRendererParams<T>) => {
    if (!params.value) return null;
    const badgeInfo = badgeMap[params.value];
    if (!badgeInfo) return params.value;
    return <Badge status={badgeInfo.status} text={badgeInfo.text} />;
  };
  return StatusCellRenderer;
};

/**
 * 상품 목록을 Popover로 표시하는 cellRenderer 생성
 */
export const createProductsCellRenderer = <T extends { products?: any[] | null }>() => {
  const ProductsCellRenderer = (params: ICellRendererParams<T>) => {
    const products = params.data?.products;
    if (!products || products.length === 0) return '-';

    const firstProductName = products[0].productName;
    const truncatedName = firstProductName.length > 10 ? `${firstProductName.substring(0, 10)}...` : firstProductName;

    const popoverContent = (
      <>
        {products.map((p, i) => (
          <div key={i}>• {p.productName}</div>
        ))}
      </>
    );

    return (
      <Popover content={popoverContent} trigger='hover' placement='topLeft'>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ cursor: 'pointer' }}>{products.length}종 •</span>
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>{truncatedName}</span>
        </span>
      </Popover>
    );
  };
  return ProductsCellRenderer;
};

/**
 * 금액을 Popover로 표시하는 cellRenderer 생성
 */
export const createAmountCellRenderer = <T extends { amount?: IssueAmountStructure | null }>() => {
  const AmountCellRenderer = (params: ICellRendererParams<T>) => {
    const amount = params.data?.amount;
    if (!amount) return <span style={{ color: '#8c8c8c' }}>-</span>;

    const { totalAmount, supplyAmount, taxAmount } = amount;
    const color = totalAmount > 0 ? '#ff4d4f' : totalAmount < 0 ? '#1890ff' : '#8c8c8c';

    const popoverContent = (
      <div style={{ minWidth: 150 }}>
        <div>총액: {formatAmount(totalAmount)}</div>
        <div>공급가: {formatAmount(supplyAmount)}</div>
        <div>세금: {formatAmount(taxAmount)}</div>
      </div>
    );

    return (
      <Popover content={popoverContent} trigger='hover' placement='topLeft'>
        <span style={{ color, fontWeight: 500, cursor: 'pointer' }}>{formatAmount(totalAmount)}</span>
      </Popover>
    );
  };
  return AmountCellRenderer;
};

/**
 * 작성자 정보를 표시하는 cellRenderer 생성
 * - creatorId로 사용자를 조회하여 이메일의 '@' 앞부분을 표시
 * - setValue를 통해 AG-Grid 행 데이터를 업데이트하여 필터링/정렬 지원
 */

interface CreatorCellContentProps extends ICellRendererParams {
  creatorId: string;
  creatorType: string;
}

const CreatorCellContent: FC<CreatorCellContentProps> = (params) => {
  const { creatorId, creatorType } = params;
  const { user, userLoading } = useUserById(creatorId);
  const value = user?.email?.split('@')[0] ?? creatorType;

  useEffect(() => {
    if (params.setValue) {
      params.setValue(value);
    }
  }, [params, value]);

  if (userLoading) return <Spin size='small' />;

  return <span>{value}</span>;
};

export const createCreatorCellRenderer = <T extends { creatorId?: string; creatorType?: any }>() => {
  const CreatorCellRenderer = (params: ICellRendererParams<T>) => {
    const creatorType = params.data?.creatorType || '-';
    const creatorId = params.data?.creatorId;

    if (!creatorId) return <span>{creatorType}</span>;

    return <CreatorCellContent {...params} creatorId={creatorId} creatorType={creatorType} />;
  };
  return CreatorCellRenderer;
};

/**
 * 이미지를 표시하는 cellRenderer 생성
 * - 이미지가 없으면 "-" 표시
 * - 이미지가 1개면 썸네일 표시
 * - 이미지가 여러 개면 첫 번째 썸네일 + "n장" 표시
 * - 클릭하면 모든 이미지 미리보기
 */
export const createImagesCellRenderer = <T extends { images?: { [imageId: string]: IssueImage } | null }>() => {
  const ImagesCellRenderer = (params: ICellRendererParams<T>) => {
    const imagesObj = params.data?.images;

    if (!imagesObj) return '-';

    // 이미지 배열로 변환하고 index 순으로 정렬
    const images = Object.values(imagesObj).sort((a, b) => a.index - b.index);

    if (images.length === 0) return '-';

    const imageCount = images.length;
    const firstImage = images[0];

    return (
      <Image.PreviewGroup>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Image
            src={firstImage.thumbnailUrl}
            preview={{
              src: firstImage.originalUrl,
            }}
            width={40}
            height={40}
            style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
          />
          {imageCount > 1 && <span style={{ fontSize: 12, color: '#8c8c8c' }}>+{imageCount - 1}장</span>}
          {/* 나머지 이미지들은 숨김 처리하고 PreviewGroup에만 포함 */}
          {images.slice(1).map((img, idx) => (
            <Image
              key={idx}
              src={img.thumbnailUrl}
              preview={{
                src: img.originalUrl,
              }}
              style={{ display: 'none' }}
            />
          ))}
        </div>
      </Image.PreviewGroup>
    );
  };
  return ImagesCellRenderer;
};

/**
 * Task 이미지를 표시하는 cellRenderer 생성 (deliveryInfo 또는 pickupInfo의 images)
 * - deliveryInfo.images 또는 pickupInfo.images 표시
 */
export const createTaskImagesCellRenderer = <
  T extends {
    deliveryInfo?: { images?: { [imageId: string]: IssueImage } | null } | null;
    pickupInfo?: { images?: { [imageId: string]: IssueImage } | null } | null;
  }
>() => {
  const TaskImagesCellRenderer = (params: ICellRendererParams<T>) => {
    const deliveryImages = params.data?.deliveryInfo?.images;
    const pickupImages = params.data?.pickupInfo?.images;

    // deliveryInfo 또는 pickupInfo 중 이미지가 있는 것 사용
    const imagesObj = deliveryImages || pickupImages;

    if (!imagesObj) return '-';

    // 이미지 배열로 변환하고 index 순으로 정렬
    const images = Object.values(imagesObj).sort((a, b) => a.index - b.index);

    if (images.length === 0) return '-';

    const imageCount = images.length;
    const firstImage = images[0];

    return (
      <Image.PreviewGroup>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Image
            src={firstImage.thumbnailUrl}
            preview={{
              src: firstImage.originalUrl,
            }}
            width={40}
            height={40}
            style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
          />
          {imageCount > 1 && <span style={{ fontSize: 12, color: '#8c8c8c' }}>+{imageCount - 1}장</span>}
          {/* 나머지 이미지들은 숨김 처리하고 PreviewGroup에만 포함 */}
          {images.slice(1).map((img, idx) => (
            <Image
              key={idx}
              src={img.thumbnailUrl}
              preview={{
                src: img.originalUrl,
              }}
              style={{ display: 'none' }}
            />
          ))}
        </div>
      </Image.PreviewGroup>
    );
  };
  return TaskImagesCellRenderer;
};

/**
 * 재고 이슈 정보를 Modal로 표시하는 cellRenderer 생성
 */
export const createInventoryIssueInfoCellRenderer = <
  T extends {
    inventoryIssueInfo?: IssueTaskInventoryIssueInfo | null;
    products?: IssueProduct[] | null;
  }
>() => {
  const InventoryIssueInfoCellRenderer = (params: ICellRendererParams<T>) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const inventoryIssueInfo = params.data?.inventoryIssueInfo;
    const taskProducts = params.data?.products;

    if (!inventoryIssueInfo) return '-';

    const productEntries = Object.entries(inventoryIssueInfo.products);
    if (productEntries.length === 0) return '-';

    // 상태별 개수 집계
    const statusCounts = productEntries.reduce((acc, [, info]) => {
      acc[info.status] = (acc[info.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // 요약 텍스트 생성
    const summaryParts: string[] = [];
    if (statusCounts.pending) summaryParts.push(`대기 ${statusCounts.pending}`);
    if (statusCounts.completed) summaryParts.push(`완료 ${statusCounts.completed}`);
    if (statusCounts.skipped) summaryParts.push(`폐기 ${statusCounts.skipped}`);
    if (statusCounts.failed) summaryParts.push(`실패 ${statusCounts.failed}`);
    const summaryText = summaryParts.join(', ');

    // Modal용 테이블 데이터
    const tableData = productEntries.map(([productId, info]) => {
      const product = taskProducts?.find((p) => p.productId === productId);
      return {
        productId,
        productName: product?.productName || productId,
        status: info.status,
        inventoryIssueId: info.inventoryIssueId,
        processedAt: info.processedAt,
      };
    });

    // 상태별 Badge 매핑
    const statusBadgeMap: Record<string, { status: any; text: string }> = {
      pending: { status: 'default', text: '대기' },
      completed: { status: 'success', text: '완료' },
      skipped: { status: 'warning', text: '폐기' },
      failed: { status: 'error', text: '실패' },
    };

    const columns = [
      {
        title: '상품명',
        dataIndex: 'productName',
        key: 'productName',
        width: 200,
      },
      {
        title: '상태',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => {
          const badgeInfo = statusBadgeMap[status];
          return badgeInfo ? <Badge status={badgeInfo.status} text={badgeInfo.text} /> : status;
        },
      },
      {
        title: '재고 이슈 ID',
        dataIndex: 'inventoryIssueId',
        key: 'inventoryIssueId',
        width: 150,
        render: (id: string | null) => id || '-',
      },
      {
        title: '처리 시각',
        dataIndex: 'processedAt',
        key: 'processedAt',
        width: 180,
        render: (date: string | null) => (date ? formatDate(new Date(date), 'yyyy-MM-dd HH:mm:ss') : '-'),
      },
    ];

    return (
      <>
        <Button type='link' size='small' onClick={() => setIsModalOpen(true)} style={{ padding: 0 }}>
          {summaryText}
        </Button>
        <Modal
          title='재고 처리 정보'
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={700}>
          <Table dataSource={tableData} columns={columns} rowKey='productId' pagination={false} size='small' bordered />
        </Modal>
      </>
    );
  };
  return InventoryIssueInfoCellRenderer;
};

/**
 * 책임자 유형별 Tag 색상 매핑
 */
const liabilityResponsibleTypeColorMap: Record<IssueLiabilityResponsibleType, string> = {
  operator: 'blue',
  partners: 'purple',
  store: 'orange',
  productSupplier: 'green',
  etc: 'default',
};

/**
 * IssueLiability 삭제 핸들러
 */
const handleDeleteLiability = async (id: string, e?: React.MouseEvent<HTMLElement>) => {
  e?.preventDefault();
  e?.stopPropagation();
  try {
    await deleteIssueLiability(id);
    message.success('삭제되었습니다.');
  } catch (error) {
    console.error('Delete failed:', error);
    message.error('삭제에 실패했습니다.');
  }
};

/**
 * IssueLiability 요약을 Tag로 표시하는 cellRenderer 생성
 * - 각 Liability를 [유형: 책임자 / 손실금액 / 메모] 형태의 Tag로 표시
 * - Tag의 X 버튼으로 삭제 가능
 * - Tag 클릭 시 onEdit 콜백 호출 (편집 모달 열기용)
 */
export const createIssueLiabilitiesCellRenderer = <T extends { _id: string; issueLiabilities?: IssueLiabilityDoc[] }>(
  onEdit?: (issueCaseId: string, liabilityId: string) => void
) => {
  const IssueLiabilitiesCellRenderer = (params: ICellRendererParams<T>) => {
    const data = params.data;
    const liabilities = data?.issueLiabilities;

    if (!liabilities || liabilities.length === 0) {
      return <span style={{ color: '#8c8c8c' }}>-</span>;
    }

    const handleTagClick = (liabilityId: string) => {
      if (onEdit && data) {
        onEdit(data._id, liabilityId);
      }
    };

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 0' }}>
        {liabilities.map((liability) => {
          const typeLabel = issueLiabilityResponsibleTypeMap[liability.responsibleType];
          const amountText = formatAmount(liability.amount.totalAmount);
          const memoText = liability.memo
            ? ` / ${liability.memo.substring(0, 10)}${liability.memo.length > 10 ? '...' : ''}`
            : '';
          const progressNoteText = liability.progressNote
            ? ` / ${liability.progressNote.substring(0, 10)}${liability.progressNote.length > 10 ? '...' : ''}`
            : '';

          const tooltipContent = (
            <div>
              <div>유형: {typeLabel}</div>
              <div>책임자 ID: {liability.responsibleId || '-'}</div>
              <div>공급가: {formatAmount(liability.amount.supplyAmount)}</div>
              <div>세금: {formatAmount(liability.amount.taxAmount)}</div>
              <div>총액: {amountText}</div>
              {liability.memo && <div>메모: {liability.memo}</div>}
              {liability.progressNote && <div>진행 노트: {liability.progressNote}</div>}
            </div>
          );

          return (
            <Tooltip key={liability._id} title={tooltipContent} placement='top'>
              <Tag
                color={liabilityResponsibleTypeColorMap[liability.responsibleType]}
                closable
                closeIcon={
                  <Popconfirm
                    title='삭제하시겠습니까?'
                    onConfirm={(e) => handleDeleteLiability(liability._id, e)}
                    okText='삭제'
                    cancelText='취소'
                    placement='top'>
                    <CloseOutlined style={{ fontSize: 10 }} />
                  </Popconfirm>
                }
                onClose={(e) => e.preventDefault()}
                onClick={() => handleTagClick(liability._id)}
                style={{ cursor: onEdit ? 'pointer' : 'default' }}>
                {typeLabel} / {amountText}
                {memoText}
                {progressNoteText}
              </Tag>
            </Tooltip>
          );
        })}
      </div>
    );
  };
  return IssueLiabilitiesCellRenderer;
};

/**
 * Task 카테고리를 표시하는 cellRenderer 생성
 * - 카테고리 한글 변환
 * - 색상 적용 옵션 (showColor)
 * - issueCaseId가 있으면 클릭하여 상세 모달 열기
 */
export const createTaskCategoryCellRenderer = <
  T extends { category?: IssueTaskCategory; issueCaseId?: string }
>(options: {
  showColor?: boolean;
  onOpenDetail: (issueCaseId: string) => void;
}) => {
  const { showColor = true, onOpenDetail } = options;

  const TaskCategoryCellRenderer = (params: ICellRendererParams<T>) => {
    const { category, issueCaseId } = params.data || {};
    if (!category) return '-';

    const label = issueTaskCategoryKr[category] || category;
    const color = showColor ? taskCategoryColorMap[category] : undefined;

    const baseStyle: React.CSSProperties = {
      color,
      fontWeight: showColor ? 500 : undefined,
    };

    if (!issueCaseId) {
      return <span style={baseStyle}>{label}</span>;
    }

    return (
      <span
        style={{
          ...baseStyle,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={() => onOpenDetail(issueCaseId)}>
        {label}
      </span>
    );
  };

  return TaskCategoryCellRenderer;
};
